import React, { forwardRef, useState, useEffect } from "react";
import clsx from "clsx";

// Base Textarea Component
interface TextareaProps {
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  label?: string;
  helperText?: string;
  error?: string;
  disabled?: boolean;
  readOnly?: boolean;
  required?: boolean;
  autoResize?: boolean;
  resize?: "none" | "both" | "horizontal" | "vertical";
  size?: "sm" | "md" | "lg";
  variant?: "default" | "success" | "error";
  maxLength?: number;
  minLength?: number;
  rows?: number;
  cols?: number;
  className?: string;
  labelClassName?: string;
  id?: string;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  (
    {
      value = "",
      onChange,
      placeholder,
      label,
      helperText,
      error,
      disabled = false,
      readOnly = false,
      required = false,
      autoResize = false,
      resize = "vertical",
      size = "md",
      variant = "default",
      maxLength,
      minLength,
      rows = 3,
      cols,
      className,
      labelClassName,
      id,
      ...props
    },
    ref,
  ) => {
    const [internalValue, setInternalValue] = useState(value);
    const [isFocused, setIsFocused] = useState(false);
    const textareaRef = React.useRef<HTMLTextAreaElement>(null);
    const textareaId = id || `textarea-${Math.random().toString(36).substr(2, 9)}`;

    // Combine refs
    React.useImperativeHandle(ref, () => textareaRef.current!);

    const sizeStyles = {
      sm: "h-16 text-sm px-3 py-2",
      md: "h-24 text-base px-4 py-3",
      lg: "h-40 text-lg px-4 py-3",
    };

    const resizeStyles = {
      none: "resize-none",
      both: "resize",
      horizontal: "resize-x",
      vertical: "resize-y",
    };

    const variantStyles = {
      default: {
        base: "border-gray-700",
        focus: "border-tracq-bright-blue ring-tracq-bright-blue",
      },
      success: {
        base: "border-green-500",
        focus: "border-green-500 ring-green-500",
      },
      error: {
        base: "border-red-500",
        focus: "border-red-500 ring-red-500",
      },
    };

    // Auto-resize functionality
    useEffect(() => {
      if (autoResize && textareaRef.current) {
        const textarea = textareaRef.current;
        textarea.style.height = "auto";
        textarea.style.height = `${textarea.scrollHeight}px`;
      }
    }, [internalValue, autoResize]);

    // Sync external value changes
    useEffect(() => {
      setInternalValue(value);
    }, [value]);

    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const newValue = e.target.value;
      setInternalValue(newValue);
      onChange?.(newValue);
    };

    const handleFocus = () => {
      setIsFocused(true);
    };

    const handleBlur = () => {
      setIsFocused(false);
    };

    const currentVariant = variantStyles[variant];
    const hasError = !!error;
    const isDisabled = disabled || readOnly;

    const textareaClasses = clsx(
      "w-full rounded-md border transition-all duration-200",
      "bg-gray-900 text-gray-200 placeholder-gray-500",
      "focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-900",
      sizeStyles[size],
      resizeStyles[resize],
      {
        // Default state
        [currentVariant.base]: !isFocused && !hasError,
        // Focus state
        [currentVariant.focus]: isFocused && !hasError,
        // Disabled state
        "bg-gray-800 text-gray-500 cursor-not-allowed": isDisabled,
        // Error state
        "border-red-500 focus:border-red-500 focus:ring-red-500": hasError,
        // Auto-resize
        "overflow-hidden": autoResize,
      },
      className,
    );

    const characterCount = maxLength ? (
      <div className="text-xs text-gray-400 mt-1">
        {internalValue.length}/{maxLength}
      </div>
    ) : null;

    return (
      <div className="flex flex-col">
        {/* Label */}
        {label && (
          <label
            htmlFor={textareaId}
            className={clsx(
              "block text-sm font-medium text-gray-300 mb-2",
              isDisabled && "text-gray-500",
              labelClassName,
            )}
          >
            {label}
            {required && <span className="text-red-400 ml-1">*</span>}
          </label>
        )}

        {/* Textarea */}
        <textarea
          ref={textareaRef}
          id={textareaId}
          value={internalValue}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          placeholder={placeholder}
          disabled={disabled}
          readOnly={readOnly}
          required={required}
          maxLength={maxLength}
          minLength={minLength}
          rows={autoResize ? 1 : rows}
          cols={cols}
          className={textareaClasses}
          {...props}
        />

        {/* Helper/Error text and character count */}
        <div className="mt-2 space-y-1">
          {(helperText || error) && (
            <div className={clsx("text-sm", error ? "text-red-400" : "text-gray-400")}>
              {error || helperText}
            </div>
          )}
          {characterCount}
        </div>
      </div>
    );
  },
);

Textarea.displayName = "Textarea";

// Auto-resize Textarea Component
interface AutoResizeTextareaProps extends Omit<TextareaProps, "autoResize" | "rows"> {
  minRows?: number;
  maxRows?: number;
}

export const AutoResizeTextarea: React.FC<AutoResizeTextareaProps> = ({
  minRows = 1,
  maxRows = 10,
  ...props
}) => {
  return <Textarea {...props} autoResize rows={minRows} />;
};

// Fixed Size Textarea Component
interface FixedTextareaProps extends Omit<TextareaProps, "resize"> {
  height?: string;
}

export const FixedTextarea: React.FC<FixedTextareaProps> = ({ height = "h-24", ...props }) => {
  return <Textarea {...props} resize="none" className={clsx(height, props.className)} />;
};

// Textarea with Character Count
interface TextareaWithCountProps extends TextareaProps {
  showCount?: boolean;
  maxLength: number;
}

export const TextareaWithCount: React.FC<TextareaWithCountProps> = ({
  showCount = true,
  maxLength,
  ...props
}) => {
  return <Textarea {...props} maxLength={maxLength} />;
};

// Textarea Group Component
interface TextareaGroupProps {
  textareas: Array<{
    id: string;
    label?: string;
    placeholder?: string;
    value?: string;
    onChange?: (value: string) => void;
    error?: string;
    helperText?: string;
    disabled?: boolean;
    required?: boolean;
    size?: "sm" | "md" | "lg";
    variant?: "default" | "success" | "error";
  }>;
  label?: string;
  description?: string;
  className?: string;
}

export const TextareaGroup: React.FC<TextareaGroupProps> = ({
  textareas,
  label,
  description,
  className,
}) => {
  return (
    <div className={clsx("space-y-4", className)}>
      {/* Group label */}
      {(label || description) && (
        <div>
          {label && <h3 className="text-sm font-medium text-gray-200">{label}</h3>}
          {description && <p className="text-xs text-gray-400 mt-1">{description}</p>}
        </div>
      )}

      {/* Textareas */}
      <div className="space-y-4">
        {textareas.map((textareaConfig) => (
          <Textarea
            key={textareaConfig.id}
            id={textareaConfig.id}
            {...(textareaConfig.label ? { label: textareaConfig.label } : {})}
            {...(textareaConfig.placeholder ? { placeholder: textareaConfig.placeholder } : {})}
            {...(textareaConfig.value !== undefined ? { value: textareaConfig.value } : {})}
            {...(textareaConfig.onChange ? { onChange: textareaConfig.onChange } : {})}
            {...(textareaConfig.error ? { error: textareaConfig.error } : {})}
            {...(textareaConfig.helperText ? { helperText: textareaConfig.helperText } : {})}
            {...(textareaConfig.disabled !== undefined
              ? { disabled: textareaConfig.disabled }
              : {})}
            {...(textareaConfig.required !== undefined
              ? { required: textareaConfig.required }
              : {})}
            {...(textareaConfig.size ? { size: textareaConfig.size } : {})}
            {...(textareaConfig.variant ? { variant: textareaConfig.variant } : {})}
          />
        ))}
      </div>
    </div>
  );
};
